import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import SbButton from '../../components/Button';
import ErrorModal from '../../components/Modal/error';
import { RootStateType } from '@Store';
import { getUploadUrls, uploadToS3, PresignedUrlsResponse } from '../CreateShipment/UserDetails/services/uploadService';
import {
  startBatchExtraction,
  getBatchExtractionStatus,
} from '../CreateShipment/UserDetails/services/extractionService';
import EditBulkShipmentModal from '../CreateShipment/UserDetails/EditBulkShipmentModal';
import { validateShipmentFields, getPendingFields, convertJobToBulkShipment } from './aiHelpers';

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.doc', '.docx', '.txt', '.csv', '.xls', '.xlsx'];

export default function AiBulkUpload() {
  const theme = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { countryList, currencyList } = useSelector((state: RootStateType) => state?.global);

  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importStatusText, setImportStatusText] = useState<string>(
    'Uploading documents and initiating AI extraction...',
  );
  const [errorModal, setErrorModal] = useState<{ open: boolean; title: string; subTitle: string | React.ReactNode }>({
    open: false,
    title: '',
    subTitle: '',
  });

  const [isBatchActive, setIsBatchActive] = useState<boolean>(false);
  const [batchCounts, setBatchCounts] = useState<{
    pending: number;
    completed: number;
    failed: number;
    needs_review: number;
  }>({ pending: 0, completed: 0, failed: 0, needs_review: 0 });
  const [bulkShipments, setBulkShipments] = useState<any[]>([]);
  const [editingShipmentIndex, setEditingShipmentIndex] = useState<number | null>(null);

  const activePollingRef = useRef<{ cancelled: boolean } | null>(null);

  useEffect(() => {
    return () => {
      if (activePollingRef.current) {
        activePollingRef.current.cancelled = true;
      }
    };
  }, []);

  const updateShipmentsFromJobs = (jobs: any[]) => {
    setBulkShipments(prev => {
      const prevMap = new Map(prev.map(s => [s.jobId, s]));

      return jobs.map((job: any) => {
        if (prevMap.has(job.jobId)) {
          const existing = prevMap.get(job.jobId)!;

          // If the job was pending/processing but is now completed/needs_review,
          // map the newly extracted fields from the API response
          const isNewlyCompleted =
            (existing.status === 'pending' || existing.status === 'processing') &&
            (job.status === 'completed' || job.status === 'needs_review');

          if (isNewlyCompleted) {
            const mapped = convertJobToBulkShipment(job, countryList);
            return {
              ...mapped,
              jobId: job.jobId,
              status: job.status,
              error: job.error,
              confidence: job.confidence,
              invalidFields: job.invalidFields,
            };
          }

          // Otherwise keep any user edits intact and only update status/metadata
          return {
            ...existing,
            status: job.status,
            error: job.error,
            confidence: job.confidence,
            invalidFields: job.invalidFields,
          };
        }

        const mapped = convertJobToBulkShipment(job, countryList);
        return {
          ...mapped,
          jobId: job.jobId,
          status: job.status,
          error: job.error,
          confidence: job.confidence,
          invalidFields: job.invalidFields,
        };
      });
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Reset file input value so re-selecting same files triggers change event
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Validation: Enforce 2 - 100 files for Bulk AI extraction
    if (files.length < 2) {
      setErrorModal({
        open: true,
        title: 'Multiple Files Required',
        subTitle:
          'Bulk AI Extraction requires at least 2 files (up to 100). For a single invoice, please use the Single Shipment flow or select 2 or more files.',
      });
      return;
    }

    if (files.length > 100) {
      setErrorModal({
        open: true,
        title: 'Limit Exceeded',
        subTitle: 'Maximum 100 files allowed for Bulk AI extraction. Please select between 2 and 100 files.',
      });
      return;
    }

    // Validate file extensions
    const invalidFiles = files.filter(f => {
      const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
      return !ALLOWED_EXTENSIONS.includes(ext);
    });

    if (invalidFiles.length > 0) {
      setErrorModal({
        open: true,
        title: 'Unsupported File Type',
        subTitle: `Files must be PDF, PNG, JPG, JPEG, DOC, DOCX, TXT, CSV, XLS, or XLSX. Unsupported: ${invalidFiles
          .map((f: File) => f.name)
          .join(', ')}`,
      });
      return;
    }

    setIsImporting(true);
    setImportStatusText(`Uploading ${files.length} documents to secure cloud storage...`);

    try {
      // 1. Get presigned upload URLs
      const filesPayload = files.map(f => ({
        fileName: f.name,
        mimeType: f.type || 'application/octet-stream',
      }));

      let uploadUrlsResponse: PresignedUrlsResponse;
      try {
        uploadUrlsResponse = await getUploadUrls(filesPayload);
      } catch (err: any) {
        console.error('❌ Multi-upload URL Request Failed:', err);
        setErrorModal({
          open: true,
          title: 'Upload Failed',
          subTitle: err.message || 'Failed to request upload URLs. Please try again.',
        });
        setIsImporting(false);
        return;
      }

      if (!uploadUrlsResponse || !uploadUrlsResponse.batchId || !Array.isArray(uploadUrlsResponse.files)) {
        console.error('❌ Unexpected upload-urls response:', uploadUrlsResponse);
        setErrorModal({
          open: true,
          title: 'Upload Failed',
          subTitle: 'The upload service returned an incomplete response.',
        });
        setIsImporting(false);
        return;
      }

      // 2. Upload every file to S3
      setImportStatusText(`Uploading ${files.length} files...`);
      try {
        await Promise.all(
          files.map((file, idx) => {
            const uploadInfo = uploadUrlsResponse.files[idx];
            return uploadToS3(uploadInfo, file);
          }),
        );
      } catch (err: any) {
        console.error('❌ S3 Batch Upload Failed:', err);
        setErrorModal({
          open: true,
          title: 'Upload Failed',
          subTitle: err.message || 'Failed to upload all files. Please check your connection and try again.',
        });
        setIsImporting(false);
        return;
      }

      // 3. Initiate batch extraction
      setImportStatusText('Starting AI document extraction...');
      const batchKeys = uploadUrlsResponse.files.map(f => f.key);
      try {
        await startBatchExtraction(uploadUrlsResponse.batchId, batchKeys);
      } catch (err: any) {
        console.error('❌ Batch Extraction Request Failed:', err);
        setErrorModal({
          open: true,
          title: 'Extraction Failed',
          subTitle: err.message || 'Failed to initiate AI batch extraction.',
        });
        setIsImporting(false);
        return;
      }

      // Activate review table view
      setIsBatchActive(true);
      setIsImporting(false);

      // Cancel previous polling if any
      if (activePollingRef.current) {
        activePollingRef.current.cancelled = true;
      }
      const cancelRef = { cancelled: false };
      activePollingRef.current = cancelRef;

      // 4. Polling loop every 20 seconds
      const pollTick = async () => {
        if (cancelRef.cancelled) return;
        try {
          const statusRes = await getBatchExtractionStatus(uploadUrlsResponse.batchId);
          if (cancelRef.cancelled) return;

          setBatchCounts(statusRes.counts || { pending: 0, completed: 0, failed: 0, needs_review: 0 });
          if (statusRes.jobs) {
            updateShipmentsFromJobs(statusRes.jobs);
          }

          if (statusRes.counts && statusRes.counts.pending > 0) {
            setTimeout(pollTick, 20000);
          } else {
            console.info('Bulk batch extraction completed:', statusRes);
          }
        } catch (err) {
          console.error('❌ Bulk Extraction Polling Tick Failed:', err);
          if (!cancelRef.cancelled) {
            setTimeout(pollTick, 20000);
          }
        }
      };

      // Run first poll immediately
      pollTick();
    } catch (err: any) {
      console.error('❌ AI Bulk Import Error:', err);
      setErrorModal({
        open: true,
        title: 'Error',
        subTitle: err.message || 'An unexpected error occurred during AI import.',
      });
      setIsImporting(false);
    }
  };

  const handleEditSave = (updatedShipment: any) => {
    if (editingShipmentIndex !== null && editingShipmentIndex >= 0) {
      const copy = [...bulkShipments];
      copy[editingShipmentIndex] = {
        ...copy[editingShipmentIndex],
        ...updatedShipment,
      };
      setBulkShipments(copy);
      setEditingShipmentIndex(null);
    }
  };

  const handleContinueToServices = () => {
    // Navigate to Shipment Details Summary screen with state
    navigate('/shipment/create-bulk/summary', { state: bulkShipments });
  };

  const handleResetUpload = () => {
    if (activePollingRef.current) {
      activePollingRef.current.cancelled = true;
    }
    setIsBatchActive(false);
    setBulkShipments([]);
    setBatchCounts({ pending: 0, completed: 0, failed: 0, needs_review: 0 });
  };

  // Calculations for KPI cards & validation
  const totalShipments = bulkShipments.length;
  const readyShipments = bulkShipments.filter(s => {
    const val = validateShipmentFields(s);
    return (
      s.status !== 'pending' &&
      s.status !== 'processing' &&
      s.status !== 'failed' &&
      s.status !== 'error' &&
      val.isValid
    );
  }).length;
  const needsAttentionShipments = totalShipments - readyShipments;
  const allShipmentsValid = totalShipments > 0 && readyShipments === totalShipments;

  return (
    <Box sx={{ width: '100%' }}>
      <ErrorModal
        icon={'/images/Boxes/NoRatesFound.svg'}
        open={errorModal.open}
        title={errorModal.title}
        subTitle={errorModal.subTitle}
        onClose={() => setErrorModal({ open: false, title: '', subTitle: '' })}
        onConfirm={() => setErrorModal({ open: false, title: '', subTitle: '' })}
        btnText="Go back"
      />

      <input
        type="file"
        ref={fileInputRef}
        multiple
        accept={ALLOWED_MIME_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Edit Modal */}
      {editingShipmentIndex !== null && bulkShipments[editingShipmentIndex] && (
        <EditBulkShipmentModal
          shipment={bulkShipments[editingShipmentIndex]}
          onClose={() => setEditingShipmentIndex(null)}
          onSave={handleEditSave}
          countryList={countryList}
          currencyList={currencyList}
        />
      )}

      {/* Uploading Spinner Overlay */}
      {isImporting && (
        <Box
          sx={{
            py: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '16px',
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          <CircularProgress size={56} sx={{ color: theme.palette.primary.main, mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 1 }}>
            Speedbox AI Processing
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center', maxWidth: 450 }}>
            {importStatusText}
          </Typography>
        </Box>
      )}

      {/* State 1: Upload Dropzone (When no batch is active) */}
      {!isBatchActive && !isImporting && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box
              sx={{
                p: { xs: 3, md: 6 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '16px',
                border: '2px dashed',
                borderColor: alpha(theme.palette.primary.main, 0.35),
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
                Upload Multi-Invoice Documents
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, textAlign: 'center', mb: 2, maxWidth: 480 }}
              >
                Select between <strong>2 and 100 invoice files</strong>. Speedbox AI will automatically extract shipper,
                consignee, package, weight, and customs details.
              </Typography>
              <Chip
                icon={
                  <AutoAwesomeIcon
                    sx={{ fontSize: '16px !important', color: `${theme.palette.primary.main} !important` }}
                  />
                }
                label="Supports 2 to 100 invoices at once"
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  mb: 3,
                }}
              />
              <SbButton
                variant="contained"
                onClick={e => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                startIcon={<CloudUploadIcon />}
              >
                Select Invoice Files
              </SbButton>
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, mt: 2 }}>
                Supported formats: PDF, PNG, JPG, JPEG, DOC, DOCX, TXT, CSV, XLS, XLSX
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* State 2: Extracted Shipments Review Table (When batch is active) */}
      {isBatchActive && !isImporting && (
        <Box sx={{ width: '100%' }}>
          {/* Header & Quick Summary Banner */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: '16px',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.15),
              background:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.dark, 0.15)
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
                      theme.palette.primary.light || theme.palette.primary.main,
                      0.08,
                    )} 100%)`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Chip
                    icon={
                      <AutoAwesomeIcon
                        sx={{ fontSize: '15px !important', color: `${theme.palette.primary.main} !important` }}
                      />
                    }
                    label="AI Multi-Invoice Extraction"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: '0.3px',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: '6px',
                      height: '24px',
                    }}
                  />
                  {batchCounts.pending > 0 && (
                    <Chip
                      label="Extracting..."
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: '24px', fontSize: '0.75rem', fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
                  Review Extracted Shipments
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Speedbox AI has parsed your uploaded documents. Verify details below and resolve missing fields before
                  selecting logistics and services.
                </Typography>
              </Grid>

              {/* Quick KPI Metric Badges */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Total Card */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderRadius: '12px',
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      minWidth: '95px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.6875rem',
                      }}
                    >
                      Total Invoices
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}
                    >
                      {totalShipments}
                    </Typography>
                  </Box>

                  {/* Ready Card */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderRadius: '12px',
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                      minWidth: '95px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.success.main,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.6875rem',
                      }}
                    >
                      Ready
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: theme.palette.success.main, lineHeight: 1.2 }}
                    >
                      {readyShipments}
                    </Typography>
                  </Box>

                  {/* Needs Action Card */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderRadius: '12px',
                      bgcolor:
                        needsAttentionShipments > 0
                          ? alpha(theme.palette.warning.main, 0.08)
                          : alpha(theme.palette.text.primary, 0.04),
                      border: `1px solid ${
                        needsAttentionShipments > 0
                          ? alpha(theme.palette.warning.main, 0.3)
                          : alpha(theme.palette.divider, 0.6)
                      }`,
                      minWidth: '95px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: needsAttentionShipments > 0 ? theme.palette.warning.main : theme.palette.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.6875rem',
                      }}
                    >
                      Needs Action
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: needsAttentionShipments > 0 ? theme.palette.warning.main : theme.palette.text.secondary,
                        lineHeight: 1.2,
                      }}
                    >
                      {needsAttentionShipments}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Live Polling Banner */}
          {batchCounts.pending > 0 && (
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              }}
            >
              <CircularProgress size={22} sx={{ color: theme.palette.primary.main, mr: 2 }} />
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                  AI Extraction in Progress... ({batchCounts.completed} completed, {batchCounts.pending} remaining)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Extracted shipments appear in real time. Completed rows are immediately reviewable and editable.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Polished Table Container */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              overflowX: 'auto',
            }}
          >
            <Table sx={{ minWidth: 700 }}>
              <TableHead sx={{ bgcolor: 'var(--mui-palette-primary-main)' }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Invoice / Shipment
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Shipper
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Consignee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Product / Boxes
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Qty
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Weight
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Total Value
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: 'var(--mui-palette-primary-contrastText)',
                      fontSize: '0.875rem',
                      py: 1.8,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkShipments.map((shipment: any, index: number) => {
                  const valErrors = validateShipmentFields(shipment);
                  const invoice = shipment.package?.invoiceNumber || `Shipment ${index + 1}`;
                  const isFailed = shipment.status === 'failed' || shipment.status === 'error';
                  const isPending = shipment.status === 'pending' || shipment.status === 'processing';
                  const isNeedsReview = !valErrors.isValid;

                  const formatDecimal = (val: any) => {
                    if (val === undefined || val === null || val === '') return '—';
                    const num = Number(val);
                    if (isNaN(num)) return String(val);
                    return num.toFixed(2);
                  };

                  // Extract product info
                  const firstBox = shipment.package?.box?.[0];
                  const firstProd = firstBox?.products?.[0];
                  const productDesc = firstProd?.description || '—';
                  const qty =
                    firstProd?.quantity !== undefined && firstProd?.quantity !== null
                      ? formatDecimal(firstProd.quantity)
                      : '—';
                  const rawWeight = firstBox?.weight;
                  const weight =
                    rawWeight !== undefined && rawWeight !== null && rawWeight !== ''
                      ? `${formatDecimal(rawWeight)} ${shipment.package?.unitOfMeasurement?.split('-')?.[0] || 'kg'}`
                      : '—';
                  const calculatedVal = firstBox?.products?.reduce(
                    (sum: number, p: any) => sum + (Number(p.quantity) || 0) * (Number(p.unitValue) || 0),
                    0,
                  );
                  const totalVal =
                    calculatedVal !== undefined && calculatedVal !== null ? calculatedVal.toFixed(2) : '—';

                  const rowAccentColor = isNeedsReview
                    ? '#f57c00'
                    : isFailed
                    ? '#d32f2f'
                    : isPending
                    ? '#7c4dff'
                    : '#2e7d32';

                  return (
                    <TableRow
                      key={shipment.jobId || index}
                      hover
                      sx={{
                        borderLeft: `4px solid ${rowAccentColor}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                      }}
                    >
                      {/* Invoice / Shipment Cell */}
                      <TableCell component="th" scope="row" sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.925rem', color: theme.palette.text.primary }}>
                          {invoice}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            sx={{
                              fontSize: '0.6875rem',
                              height: '20px',
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.text.primary, 0.06),
                              color: theme.palette.text.secondary,
                              borderRadius: '4px',
                            }}
                          />
                          {shipment.package?.userReferenceNumber && (
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}
                            >
                              Ref: {shipment.package.userReferenceNumber}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Shipper Cell */}
                      <TableCell sx={{ py: 2 }}>
                        {shipment.shipper?.name ? (
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                              {shipment.shipper.name}
                            </Typography>
                            {shipment.shipper.companyName && (
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '0.75rem' }}
                              >
                                {shipment.shipper.companyName}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '0.775rem' }}
                            >
                              {[shipment.shipper.city, shipment.shipper.pincode, shipment.shipper.country]
                                .filter(Boolean)
                                .join(', ') || '—'}
                            </Typography>
                            {shipment.shipper.phoneNumber && (
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.725rem' }}
                              >
                                {shipment.shipper.telephoneCode} {shipment.shipper.phoneNumber}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: theme.palette.warning.main, fontWeight: 500 }}>
                            ⚠️ Missing Shipper
                          </Typography>
                        )}
                      </TableCell>

                      {/* Consignee Cell */}
                      <TableCell sx={{ py: 2 }}>
                        {shipment.consignee?.name ? (
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                              {shipment.consignee.name}
                            </Typography>
                            {shipment.consignee.companyName && (
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '0.75rem' }}
                              >
                                {shipment.consignee.companyName}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '0.775rem' }}
                            >
                              {[shipment.consignee.city, shipment.consignee.pincode, shipment.consignee.country]
                                .filter(Boolean)
                                .join(', ') || '—'}
                            </Typography>
                            {shipment.consignee.phoneNumber && (
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.725rem' }}
                              >
                                {shipment.consignee.telephoneCode} {shipment.consignee.phoneNumber}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: theme.palette.warning.main, fontWeight: 500 }}>
                            ⚠️ Missing Consignee
                          </Typography>
                        )}
                      </TableCell>

                      {/* Product / Boxes Cell */}
                      <TableCell sx={{ py: 2 }}>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: theme.palette.text.primary,
                              maxWidth: '180px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={productDesc}
                          >
                            {productDesc}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}
                          >
                            {shipment.package?.box?.length || 1} Box(es)
                            {firstProd?.HSNCode ? ` • HSN: ${firstProd.HSNCode}` : ''}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Qty Cell */}
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                          {qty}
                        </Typography>
                      </TableCell>

                      {/* Weight Cell */}
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                          {weight}
                        </Typography>
                      </TableCell>

                      {/* Total Value Cell */}
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                          {totalVal !== '—' ? `${shipment.package?.currency || ''} ${totalVal}` : '—'}
                        </Typography>
                      </TableCell>

                      {/* Status Cell */}
                      <TableCell sx={{ py: 2 }}>
                        {isPending ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} sx={{ color: '#7c4dff' }} />
                            <Typography variant="caption" sx={{ color: '#7c4dff', fontWeight: 600 }}>
                              Processing...
                            </Typography>
                          </Box>
                        ) : isFailed ? (
                          <Box>
                            <Chip
                              icon={<ErrorOutlineIcon sx={{ fontSize: '15px !important' }} />}
                              label="Failed"
                              size="small"
                              color="error"
                              sx={{ fontWeight: 700 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.error.main, display: 'block', mt: 0.5, fontSize: '0.725rem' }}
                            >
                              {shipment.error || 'Extraction failed'}
                            </Typography>
                          </Box>
                        ) : isNeedsReview ? (
                          <Box>
                            <Chip
                              icon={<WarningAmberIcon sx={{ fontSize: '15px !important' }} />}
                              label="Needs Action"
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 700 }}
                            />
                            {(() => {
                              const pending = getPendingFields(shipment);
                              return (
                                <Box sx={{ mt: 0.75, maxWidth: '240px' }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.palette.warning.dark || '#b78103',
                                      fontWeight: 600,
                                      display: 'block',
                                      fontSize: '0.725rem',
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    Missing: {pending.slice(0, 3).join(', ')}
                                    {pending.length > 3 ? ` +${pending.length - 3} more` : ''}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </Box>
                        ) : (
                          <Box>
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: '15px !important' }} />}
                              label="Ready"
                              size="small"
                              color="success"
                              sx={{ fontWeight: 700 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mt: 0.4,
                                fontSize: '0.725rem',
                              }}
                            >
                              All details verified
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      {/* Action Cell */}
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => setEditingShipmentIndex(index)}
                          disabled={isPending}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 1.5,
                            borderColor: alpha(theme.palette.primary.main, 0.4),
                            color: theme.palette.primary.main,
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                            },
                          }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Navigation actions */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
              mb: 2,
              p: 2,
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              bgcolor: theme.palette.background.paper,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <SbButton variant="outlined" onClick={handleResetUpload} startIcon={<ArrowBackIcon />}>
              Upload Other Files
            </SbButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!allShipmentsValid && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.warning.dark || '#b78103',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  ⚠️ Please resolve required fields in all shipments before continuing
                </Typography>
              )}
              <SbButton
                variant="contained"
                onClick={handleContinueToServices}
                disabled={!allShipmentsValid}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  py: 1,
                  px: 3,
                  fontWeight: 700,
                  borderRadius: '8px',
                  boxShadow: allShipmentsValid ? `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                }}
              >
                Continue to Services
              </SbButton>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
